/**
 * Géométrie du schéma 3D.
 *
 * Ce module n'importe rien — un test lit le fichier et le vérifie. Il ne peut
 * donc ni lire un dataset, ni appeler une éphéméride, ni inventer une valeur :
 * tout ce qu'il dessine lui est **donné**, sous forme de nombres sans unité,
 * par l'appelant qui les tient d'un modèle.
 *
 * ## Le dessin est un rendu de la prédiction, pas une illustration
 *
 * L'entrée qui compte est `shadowRatio` : la longueur d'ombre divisée par la
 * hauteur du gnomon, telle qu'un modèle la prédit. Le gnomon dessiné est
 * démesurément grand — à l'échelle, un bâton d'un mètre sur une Terre de
 * 6 371 km serait invisible — mais l'ombre dessinée vaut exactement
 * `hauteur dessinée × shadowRatio`. **Le rapport est fidèle, l'échelle ne
 * l'est pas**, et c'est cette fidélité-là qu'un test verrouille : le schéma
 * dit la même chose que le chiffre, comme la table dit la même chose que le
 * graphe.
 *
 * ## Ce qu'il ne fait pas
 *
 * Il ne départage rien. Les deux géométries rendent compte des mêmes
 * observations, et c'est précisément pourquoi un dessin ne tranche pas.
 *
 * Hors du noyau : nombres nus et `Math` autorisés (les unités ici sont des
 * unités de scène, pas des grandeurs physiques).
 */

export type SchematicGeometry = "spherical" | "flat";

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Ce que l'appelant doit fournir. Aucun de ces nombres n'est décidé ici.
 */
export interface SchematicRequest {
  readonly geometry: SchematicGeometry;
  /** Latitude de l'observateur, en degrés. */
  readonly latitude: number;
  /** Latitude du point subsolaire — la déclinaison du Soleil —, en degrés. */
  readonly subsolarLatitude: number;
  /**
   * Longueur d'ombre divisée par hauteur de gnomon, telle que le modèle la
   * prédit. `undefined` quand le modèle ne prédit pas d'ombre : Soleil sous
   * l'horizon. On ne dessine alors aucune ombre — on n'en invente pas.
   */
  readonly shadowRatio: number | undefined;
}

export interface GnomonSketch {
  readonly id: string;
  /** Pied, sur le sol. */
  readonly base: Vec3;
  /** Sommet. Le segment base → sommet est perpendiculaire au sol local. */
  readonly top: Vec3;
  /** Extrémité de l'ombre. Absente quand le modèle n'en prédit pas. */
  readonly shadowEnd: Vec3 | undefined;
}

export interface RaySketch {
  readonly from: Vec3;
  readonly to: Vec3;
}

export type GroundSketch =
  | { readonly kind: "sphere"; readonly radius: number }
  | { readonly kind: "disc"; readonly radius: number };

export interface SunSketch {
  readonly position: Vec3;
  readonly radius: number;
  /** Vrai si le Soleil est un objet local de la scène, faux s'il est « très loin ». */
  readonly local: boolean;
}

export interface Schematic {
  readonly geometry: SchematicGeometry;
  readonly ground: GroundSketch;
  readonly sun: SunSketch;
  readonly gnomons: readonly GnomonSketch[];
  readonly rays: readonly RaySketch[];
}

// ── Constantes de mise en scène ──────────────────────────────────────────────
// Aucune n'est une grandeur physique : ce sont des unités de dessin.

const EARTH_RADIUS = 1;
const DISC_RADIUS = 1.15;
/** Hauteur de gnomon dessinée, très exagérée pour rester visible. */
const GNOMON = 0.26;
/** Au-delà, une ombre rasante ferait le tour du globe et brouillerait tout. */
const MAX_DRAWN_SHADOW = 1.2;
const FAR_SUN_DISTANCE = 1.75;
const FAR_SUN_RADIUS = 0.15;
const LOCAL_SUN_RADIUS = 0.075;
/** Hauteur du Soleil plat quand aucune ombre n'est prédite : rien à en déduire. */
const FALLBACK_SUN_HEIGHT = 0.75;
const RAY_COUNT = 9;
const BEAM_HALF_WIDTH = 1.05;
const RAY_LENGTH = FAR_SUN_DISTANCE + EARTH_RADIUS * 0.9;

/**
 * Azimut du plan méridien où se joue toute la scène, mesuré depuis l'axe z.
 *
 * Ératosthène est un problème plan : gnomons, rayons et Soleil tiennent dans un
 * seul méridien. Reste à choisir son orientation. Face à la caméra, les angles
 * seraient les plus lisibles mais la sphère ressemblerait à un disque ; de
 * profil, les gnomons partiraient au limbe et on ne verrait plus qu'ils sont
 * debout. Cette valeur est le compromis.
 */
const MERIDIAN_AZIMUTH = 75;

const DEG = Math.PI / 180;

// ── Vecteurs ─────────────────────────────────────────────────────────────────

function vec(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

function scale(v: Vec3, k: number): Vec3 {
  return vec(v.x * k, v.y * k, v.z * k);
}

function add(a: Vec3, b: Vec3): Vec3 {
  return vec(a.x + b.x, a.y + b.y, a.z + b.z);
}

function subtract(a: Vec3, b: Vec3): Vec3 {
  return vec(a.x - b.x, a.y - b.y, a.z - b.z);
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function norm(v: Vec3): number {
  return Math.sqrt(dot(v, v));
}

export function distance(a: Vec3, b: Vec3): number {
  return norm(subtract(a, b));
}

const MERIDIAN: Vec3 = vec(
  Math.sin(MERIDIAN_AZIMUTH * DEG),
  0,
  Math.cos(MERIDIAN_AZIMUTH * DEG),
);
const POLAR: Vec3 = vec(0, 1, 0);

/** Point du plan méridien, de coordonnées `(horizontale, verticale)` dans ce plan. */
function inMeridian(horizontal: number, vertical: number): Vec3 {
  return add(scale(MERIDIAN, horizontal), scale(POLAR, vertical));
}

/** Normale sortante à la latitude donnée. */
function outward(latitudeDegrees: number): Vec3 {
  return inMeridian(Math.cos(latitudeDegrees * DEG), Math.sin(latitudeDegrees * DEG));
}

/**
 * Longueur d'ombre à dessiner. Bornée pour rester lisible : une ombre rasante
 * est arbitrairement longue et ferait le tour de la scène. Le plafond est
 * visible — l'ombre touche le bord — et jamais silencieux au point de laisser
 * croire à une valeur juste.
 */
function drawnShadow(ratio: number | undefined): number | undefined {
  if (ratio === undefined || !Number.isFinite(ratio) || ratio < 0) return undefined;
  return Math.min(GNOMON * ratio, MAX_DRAWN_SHADOW);
}

/** Le sens dans lequel l'ombre fuit : à l'opposé du point subsolaire. */
function shadowSign(latitude: number, subsolarLatitude: number): number {
  return latitude >= subsolarLatitude ? 1 : -1;
}

// ── Terre sphérique, Soleil lointain ─────────────────────────────────────────

function sphericalSchematic(request: SchematicRequest): Schematic {
  const sunDirection = outward(request.subsolarLatitude);
  const travel = scale(sunDirection, -1);
  const across = inMeridian(
    Math.sin(request.subsolarLatitude * DEG),
    -Math.cos(request.subsolarLatitude * DEG),
  );

  const shadow = drawnShadow(request.shadowRatio);
  const sign = shadowSign(request.latitude, request.subsolarLatitude);

  const gnomonAt = (latitude: number, id: string, length: number | undefined): GnomonSketch => {
    const normal = outward(latitude);
    const base = scale(normal, EARTH_RADIUS);
    const top = scale(normal, EARTH_RADIUS + GNOMON);
    // L'ombre suit le sol courbe : une longueur d'arc, sur une sphère de rayon
    // 1, est directement un angle en radians.
    const end =
      length === undefined
        ? undefined
        : scale(outward(latitude + (sign * length) / DEG), EARTH_RADIUS);
    return { id, base, top, shadowEnd: end };
  };

  const gnomons = [gnomonAt(request.latitude, "observateur", shadow)];
  // Le point subsolaire : là où un gnomon ne porterait aucune ombre. C'est la
  // figure d'Ératosthène, et elle se déplace avec la date.
  if (Math.abs(request.latitude - request.subsolarLatitude) > 0.5) {
    gnomons.push(gnomonAt(request.subsolarLatitude, "subsolaire", 0));
  }

  const rays = Array.from({ length: RAY_COUNT }, (_, index) => {
    const offset = -BEAM_HALF_WIDTH + (2 * BEAM_HALF_WIDTH * index) / (RAY_COUNT - 1);
    const from = add(scale(travel, -FAR_SUN_DISTANCE), scale(across, offset));
    const to = hitSphere(from, travel, EARTH_RADIUS) ?? add(from, scale(travel, RAY_LENGTH));
    return { from, to };
  });

  return {
    geometry: "spherical",
    ground: { kind: "sphere", radius: EARTH_RADIUS },
    sun: {
      position: scale(sunDirection, FAR_SUN_DISTANCE),
      radius: FAR_SUN_RADIUS,
      local: false,
    },
    gnomons,
    rays,
  };
}

/** Premier point où la demi-droite rencontre la sphère centrée à l'origine. */
function hitSphere(origin: Vec3, direction: Vec3, radius: number): Vec3 | undefined {
  const b = 2 * dot(origin, direction);
  const c = dot(origin, origin) - radius * radius;
  const discriminant = b * b - 4 * c;
  if (discriminant < 0) return undefined;
  const t = (-b - Math.sqrt(discriminant)) / 2;
  if (t <= 0) return undefined;
  return add(origin, scale(direction, t));
}

// ── Plan horizontal, Soleil local ────────────────────────────────────────────

/**
 * Carte plate usuelle : pôle au centre, distance au centre proportionnelle à la
 * colatitude. Même convention que le modèle plat structuré du noyau.
 */
function flatRadius(latitudeDegrees: number): number {
  return (90 - latitudeDegrees) / 90;
}

function flatSchematic(request: SchematicRequest): Schematic {
  const observerRadius = flatRadius(request.latitude);
  const subsolarRadius = flatRadius(request.subsolarLatitude);
  const shadow = drawnShadow(request.shadowRatio);
  const sign = shadowSign(request.latitude, request.subsolarLatitude);

  const base = inMeridian(observerRadius, 0);
  const top = add(base, scale(POLAR, GNOMON));
  // L'ombre fuit le point subsolaire, en s'éloignant du centre si l'observateur
  // est plus près du pôle que lui.
  const end =
    shadow === undefined ? undefined : inMeridian(observerRadius - sign * shadow, 0);

  // Le Soleil est placé là où ses rayons rasent effectivement le sommet du
  // gnomon pour aboutir à l'ombre dessinée. Sans ce calage, la scène montrerait
  // des rayons qui manquent l'ombre qu'elle prétend expliquer.
  //
  // Construction exacte, `Hs = h · (1 + d / L)`, et non l'approximation
  // `Hs = h · d / L` du modèle : celle-ci suppose le gnomon négligeable devant
  // la hauteur du Soleil, ce qui est vrai dans le monde et faux dans ce dessin,
  // où le gnomon est démesuré exprès.
  // Pas de plancher défensif sur cette hauteur : la formule garantit déjà que
  // le Soleil est au-dessus du sommet, et un `Math.max` cassait la construction
  // sans bruit dès que l'ombre devenait longue.
  const span = Math.abs(observerRadius - subsolarRadius);
  const degenerate = shadow === undefined || shadow === 0 || span < 1e-9;
  const sunHeight = degenerate ? FALLBACK_SUN_HEIGHT : GNOMON * (1 + span / shadow);
  const sun = inMeridian(subsolarRadius, sunHeight);

  const gnomons: GnomonSketch[] = [
    { id: "observateur", base, top, shadowEnd: end },
  ];
  if (Math.abs(request.latitude - request.subsolarLatitude) > 0.5) {
    const subsolarBase = inMeridian(subsolarRadius, 0);
    gnomons.push({
      id: "subsolaire",
      base: subsolarBase,
      top: add(subsolarBase, scale(POLAR, GNOMON)),
      shadowEnd: subsolarBase,
    });
  }

  const rays = Array.from({ length: RAY_COUNT }, (_, index) => {
    const along = -DISC_RADIUS + (2 * DISC_RADIUS * index) / (RAY_COUNT - 1);
    return { from: sun, to: inMeridian(along, 0) };
  });

  return {
    geometry: "flat",
    ground: { kind: "disc", radius: DISC_RADIUS },
    sun: { position: sun, radius: LOCAL_SUN_RADIUS, local: true },
    gnomons,
    rays,
  };
}

export function buildSchematic(request: SchematicRequest): Schematic {
  return request.geometry === "spherical"
    ? sphericalSchematic(request)
    : flatSchematic(request);
}

/** Hauteur de gnomon dessinée. Sert aux tests de fidélité du rapport. */
export const DRAWN_GNOMON = GNOMON;
export const MAX_SHADOW = MAX_DRAWN_SHADOW;

/** Longueur de l'ombre dessinée. Une longueur de dessin, pas une prédiction. */
export function drawnShadowLength(gnomon: GnomonSketch): number {
  if (gnomon.shadowEnd === undefined) return 0;
  return distance(gnomon.shadowEnd, gnomon.base);
}

/** Nombre de points échantillonnés sur le sol pour en connaître l'étendue vue. */
const GROUND_SAMPLES = 32;

/**
 * Les points que la scène occupe réellement.
 *
 * Cadrer sur les huit sommets de la boîte englobante paraissait équivalent et
 * ne l'était pas : les coins d'une boîte autour d'une sphère sont du vide, et
 * les faire tenir dans le cadre repoussait la caméra jusqu'à ne plus remplir
 * qu'un quart de la largeur. On échantillonne donc le sol au lieu de le
 * majorer.
 */
export function schematicPoints(schematic: Schematic): readonly Vec3[] {
  const points: Vec3[] = [
    schematic.sun.position,
    ...schematic.rays.flatMap((ray) => [ray.from, ray.to]),
    ...schematic.gnomons.flatMap((g) =>
      g.shadowEnd === undefined ? [g.base, g.top] : [g.base, g.top, g.shadowEnd],
    ),
  ];

  const { radius } = schematic.ground;
  for (let index = 0; index < GROUND_SAMPLES; index += 1) {
    const angle = (2 * Math.PI * index) / GROUND_SAMPLES;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    if (schematic.ground.kind === "sphere") {
      points.push(
        vec(radius * cosine, radius * sine, 0),
        vec(radius * cosine, 0, radius * sine),
        vec(0, radius * cosine, radius * sine),
      );
    } else {
      points.push(vec(radius * cosine, 0, radius * sine));
    }
  }

  return points;
}

export interface Bounds {
  readonly min: Vec3;
  readonly max: Vec3;
}

export function schematicBounds(schematic: Schematic): Bounds {
  const points = schematicPoints(schematic);

  const min = vec(Infinity, Infinity, Infinity);
  const max = vec(-Infinity, -Infinity, -Infinity);
  return points.reduce<Bounds>(
    (bounds, point) => ({
      min: vec(
        Math.min(bounds.min.x, point.x),
        Math.min(bounds.min.y, point.y),
        Math.min(bounds.min.z, point.z),
      ),
      max: vec(
        Math.max(bounds.max.x, point.x),
        Math.max(bounds.max.y, point.y),
        Math.max(bounds.max.z, point.z),
      ),
    }),
    { min, max },
  );
}

export function boundsCentre(bounds: Bounds): Vec3 {
  return scale(add(bounds.min, bounds.max), 0.5);
}
